import React, { useEffect, useState, useMemo, useRef } from "react";
import { api, formatErr, fmtMoney, API, downloadCsv } from "@/lib/api";
import { printBarcodeLabels } from "@/lib/printLabels";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { useFeature } from "@/contexts/FeaturesContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, AlertTriangle, Printer, ScanBarcode, Camera, Upload, Sparkles, FolderInput, Share2, Download, Percent, Copy, ClipboardList, DollarSign, ClipboardPaste, X } from "lucide-react";
import BarcodeScannerDialog from "@/components/BarcodeScannerDialog";
import AIImportProducts from "@/pages/inventory/AIImportProducts";
import BulkSelectionBar, { useBulkSelection, SelectAllHeader, RowCheckbox } from "@/components/BulkSelectionBar";
import { CategoryIcon } from "@/components/CategoryIconPicker";
import Barcode from "react-barcode";
import ProductRow from "@/pages/sales/components/ProductRow";
import BulkProductDialog from "@/pages/sales/components/BulkProductDialog";
import QuickProductDialog from "@/pages/sales/components/QuickProductDialog";
import BulkPriceDialog from "@/pages/sales/components/BulkPriceDialog";
import ProductFormDialog from "@/pages/sales/components/ProductFormDialog";

const MEDIA_BASE = process.env.REACT_APP_BACKEND_URL || "";
const imgSrc = (u) => (u ? `${MEDIA_BASE}${u}` : "");

const isWeightUnitName = (name) => {
  const n = (name || "").toString().trim().toLowerCase();
  return ["كيلو", "كغ", "كجم", "kg", "kilo", "فرط", "وزن"].some(k => n.includes(k));
};

const empty = { name:"", barcode:"", plu:"", category:"", cost_price:0, sale_price:0, currency:"SYP", stock:0, min_stock:0, units:[{name:"قطعة", factor:1}], expiry_date:"", batch_number:"", is_pharma:false, requires_prescription:false, controlled:false, pharmaceutical_form:"", strength:"", active_ingredient:"", package_size:"", volume:"", manufacturer:"", purchase_date:"", sale_date:"", pharma_status:"available", description:"", image_url:"", image_urls:[], options:[], variants:[] };
const MOCK_MODE = process.env.REACT_APP_MOCK_MODE === "true";
const MOCK_PRODUCTS_KEY = "muhasabaty:mock:products";
const MOCK_CATALOG_KEY = "muhasabaty:mock:barcode-catalog";
const readMock = (key, fallback) => { try { return JSON.parse(localStorage.getItem(key) || "null") || fallback; } catch { return fallback; } };
const writeMock = (key, value) => localStorage.setItem(key, JSON.stringify(value));

export default function Products() {
  const { t } = useI18n();
  const { companyId, currentCompany } = useAuth();
  const quickAddEnabled = useFeature("quick_product_add");
  const bulkAddEnabled = useFeature("bulk_add");
  const bulkImportEnabled = useFeature("bulk_import");
  const aiImportEnabled = useFeature("ai_import");
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const quickEmpty = { name:"", sale_price:"", cost_price:"", barcode:"", plu:"", category:"", stock:"" };
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickForm, setQuickForm] = useState(quickEmpty);
  const [quickCount, setQuickCount] = useState(0);
  const [quickSaving, setQuickSaving] = useState(false);
  const quickNameRef = useRef(null);
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [formImage, setFormImage] = useState({ preview: "", b64: "", type: "" });
  const [savingImg, setSavingImg] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanForFormOpen, setScanForFormOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [assignCat, setAssignCat] = useState("");
  const [priceOpen, setPriceOpen] = useState(false);
  const [priceMode, setPriceMode] = useState("percent");
  const [pricePct, setPricePct] = useState(10);
  const [priceAmount, setPriceAmount] = useState("");
  const [priceDirection, setPriceDirection] = useState("increase");
  const [priceTarget, setPriceTarget] = useState("sale");
  const [priceRound, setPriceRound] = useState(0);
  const [priceReason, setPriceReason] = useState("");
  const [rates, setRates] = useState({ USD: 1, SYP: 1 });
  const bulk = useBulkSelection(items.map(x => x.id));
  const selectedProducts = useMemo(() => items.filter(p => bulk.selected.has(p.id)), [items, bulk.selected]);
  const selectedCurrencies = useMemo(() => [...new Set(selectedProducts.map(p => (p.currency || "SYP").toUpperCase()))], [selectedProducts]);
  const priceCurrency = selectedCurrencies.length === 1 ? selectedCurrencies[0] : (currentCompany?.base_currency || "SYP").toUpperCase();
  const mixedPriceCurrencies = selectedCurrencies.length > 1;
  const previewAdjustedPrice = (rawPrice, currency) => {
    const value = Number(rawPrice) || 0;
    let next = priceMode === "percent"
      ? value * (1 + (Number(pricePct) || 0) / 100)
      : value + (priceDirection === "increase" ? 1 : -1) * (Number(priceAmount) || 0);
    if (+priceRound > 0 && !(+priceRound === 1 && (currency || "SYP").toUpperCase() !== "SYP")) {
      next = Math.round(next / +priceRound) * +priceRound;
    }
    return Math.max(0, Math.round(next * 100) / 100);
  };

  const load = async () => {
    if (MOCK_MODE) {
      const cid = localStorage.getItem("company_id") || "demo-pharma-a";
      // Seed the selected demo company before reading its browser-local data.
      // Otherwise a fresh preview could show zero products on first visit.
      await api.get("/products");
      const all = readMock(MOCK_PRODUCTS_KEY, {});
      const rows = (all[cid] || []).filter(p => !search || `${p.name} ${p.barcode}`.toLowerCase().includes(search.toLowerCase()));
      const demoCategories = readMock(`muhasabaty:mock:${cid}:categories`, []);
      setItems(rows); setCategories(demoCategories); return;
    }
    try {
      const [prods, cats] = await Promise.all([
        api.get("/products", { params: search ? { search } : {} }),
        api.get("/categories").catch(() => ({ data: [] })),
      ]);
      setItems(prods.data);
      setCategories(cats.data || []);
    }
    catch(e){ toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { api.get("/currencies").then(({data}) => setRates(data.rates || {})).catch(() => {}); }, [companyId]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search, companyId]); // eslint-disable-line
  useEffect(() => { bulk.clear(); }, [search, companyId, bulk.clear]);

  const openNew = () => { setEditing(null); setForm({...empty}); setFormImage({ preview: "", b64: "", type: "" }); setOpen(true); };
  const [reorderOpen, setReorderOpen] = useState(false);
  const [reorderItems, setReorderItems] = useState([]);
  const openReorder = async () => {
    setReorderOpen(true); setReorderItems([]);
    try { const { data } = await api.get("/products/reorder-list"); setReorderItems(data || []); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const printReorder = () => {
    const rows = reorderItems.map(x => `<tr><td>${x.name}</td><td style="text-align:center">${x.stock}</td><td style="text-align:center">${x.min_stock}</td><td style="text-align:center;font-weight:bold">${x.suggested}</td></tr>`).join("");
    const w = window.open("", "_blank");
    w.document.write(`<html dir="rtl"><head><meta charset="utf-8"><title>طلب شراء — النواقص</title><style>body{font-family:Tajawal,Arial;padding:24px}h2{margin:0}table{width:100%;border-collapse:collapse;margin-top:12px}th,td{border:1px solid #ddd;padding:8px;font-size:13px}th{background:#f1f5f9}</style></head><body><h2>طلب شراء — الأصناف الناقصة</h2><div>${new Date().toLocaleString("ar-EG")}</div><table><thead><tr><th>الصنف</th><th>المتوفر</th><th>الحد الأدنى</th><th>الكمية المطلوبة</th></tr></thead><tbody>${rows}</tbody></table></body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };
  const openEdit = (p) => { setEditing(p); setForm({...empty, ...p, expiry_date: p.expiry_date || "", units: p.units?.length ? p.units : [{name:"قطعة", factor:1}] }); setFormImage({ preview: p.image_url ? imgSrc(p.image_url) : "", b64: "", type: "" }); setOpen(true); };
  // Duplicate: open the form as a NEW product prefilled from an existing one (name suffixed, barcode cleared)
  const duplicateProduct = (p) => {
    setEditing(null);
    setForm({ ...empty, ...p, id: undefined, name: `${p.name} (نسخة)`, barcode: "", units: p.units?.length ? p.units : [{name:"قطعة", factor:1}] });
    setFormImage({ preview: p.image_url ? imgSrc(p.image_url) : "", b64: "", type: "" });
    setOpen(true);
    toast.success("عدّل ثم احفظ لإنشاء صنف جديد");
  };

  // Currency options = company base + enabled currencies + common defaults (no duplicates)
  const currencyOptions = useMemo(() => {
    const out = [];
    const push = (c) => { const u = (c || "").toUpperCase(); if (u && !out.includes(u)) out.push(u); };
    push(currentCompany?.base_currency);
    (currentCompany?.enabled_currencies || []).forEach(push);
    ["SYP", "USD", "TRY", "EUR"].forEach(push);
    return out;
  }, [currentCompany]);

  const sellByWeight = isWeightUnitName(form.units?.[0]?.name);
  const toggleSellByWeight = (on) => {
    setForm(f => ({ ...f, units: on ? [{ name: "كيلو غرام", factor: 1 }] : [{ name: "قطعة", factor: 1 }] }));
  };

  const profit = (+form.sale_price || 0) - (+form.cost_price || 0);
  const margin = (+form.cost_price || 0) > 0 ? (profit / (+form.cost_price)) * 100 : 0;

  const pickFormImage = async (file) => {
    if (!file) return;
    if (file.size > 1024 * 1024) { toast.error("الحد الأقصى 1MB"); return; }
    const b64 = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result.split(",")[1]); fr.readAsDataURL(file); });
    setFormImage({ preview: URL.createObjectURL(file), b64, type: file.type || "image/png" });
  };

  const handleScanned = async (code) => {
    setScanOpen(false);
    const clean = code.trim();
    if (MOCK_MODE) {
      const cid = localStorage.getItem("company_id") || "demo-pharma-a";
      const own = readMock(MOCK_PRODUCTS_KEY, {})[cid] || [];
      const found = own.find(p => p.barcode === clean);
      if (found) { openEdit(found); return; }
      const shared = readMock(MOCK_CATALOG_KEY, {})[clean];
      setEditing(null); setForm({...empty, ...(shared || {}), barcode: clean, cost_price: 0, sale_price: 0, stock: 0}); setOpen(true);
      toast.info(shared ? "تم جلب بيانات الدواء العامة من الفهرس المشترك — أدخل سعر ومخزون شركتك" : "باركود جديد — أكمل تفاصيل الدواء");
      return;
    }
    try {
      const { data } = await api.get(`/products/barcode/${encodeURIComponent(clean)}`);
      // Product exists in THIS company — offer edit
      toast.info(`الصنف موجود: ${data.name} — فتح للتعديل`);
      openEdit(data);
    } catch(e) {
      if (e?.response?.status === 404) {
        // Not in this company — check the HIDDEN cross-company catalog for the name
        let name = "";
        try {
          const { data: g } = await api.get(`/global-products/lookup`, { params: { barcode: clean } });
          if (g?.found) name = g.name || "";
        } catch {}
        setEditing(null);
        setForm({...empty, barcode: clean, name});
        setOpen(true);
        toast.success(name
          ? `باركود معروف من الفهرس المشترك: «${name}» — أدخل سعر ومخزون شركتك`
          : "باركود جديد — أكمل تفاصيل الصنف");
      } else {
        toast.error(formatErr(e));
      }
    }
  };

  // When a barcode is typed/pasted manually and the name is still empty,
  // auto-fill it from the hidden cross-company catalog.
  const lookupGlobalBarcode = async (code) => {
    const clean = (code || "").trim();
    if (!clean || form.name?.trim()) return;
    try {
      const { data: g } = await api.get(`/global-products/lookup`, { params: { barcode: clean } });
      if (g?.found && g.name) {
        setForm(f => (f.name?.trim() ? f : { ...f, name: g.name }));
        toast.info(`تم جلب الاسم «${g.name}» من الفهرس المشترك`);
      }
    } catch {}
  };

  const save = async () => {
    try {
      const body = {...form, cost_price: +form.cost_price, sale_price:+form.sale_price, stock:+form.stock, min_stock:+form.min_stock, expiry_date: form.expiry_date || null, image_urls: (form.image_urls || []).filter(Boolean).slice(0, 8), options: (form.options || []).filter(x => x?.name && x?.values?.length).slice(0, 10) };
      if (MOCK_MODE) {
        const cid = localStorage.getItem("company_id") || "demo-pharma-a";
        const all = readMock(MOCK_PRODUCTS_KEY, {}); const rows = all[cid] || [];
        const saved = {...body, id: editing?.id || `mock-${Date.now()}`};
        all[cid] = editing ? rows.map(p => p.id === editing.id ? saved : p) : [...rows, saved]; writeMock(MOCK_PRODUCTS_KEY, all);
        if (saved.barcode) {
          const catalog = readMock(MOCK_CATALOG_KEY, {});
          catalog[saved.barcode] = { name:saved.name, barcode:saved.barcode, category:saved.category, is_pharma:true, pharmaceutical_form:saved.pharmaceutical_form, strength:saved.strength, active_ingredient:saved.active_ingredient, package_size:saved.package_size, volume:saved.volume, manufacturer:saved.manufacturer, units:saved.units };
          writeMock(MOCK_CATALOG_KEY, catalog);
        }
        toast.success("تم حفظ الدواء محليًا وإضافته للفهرس المشترك"); setOpen(false); load(); return;
      }
      let targetId = editing?.id;
      if (editing) await api.put(`/products/${editing.id}`, body);
      else { const { data } = await api.post("/products", body); targetId = data?.id; }
      // Upload a picked image (if any) to the product's image endpoint
      if (formImage.b64 && targetId) {
        try { await api.post(`/products/${targetId}/image`, { content_type: formImage.type, data_b64: formImage.b64 }); }
        catch (e) { toast.error("تعذّر رفع الصورة: " + formatErr(e)); }
      }
      toast.success("تم الحفظ"); setOpen(false); setFormImage({ preview: "", b64: "", type: "" }); load();
    } catch(e){ toast.error(formatErr(e)); }
  };

  const quickSave = async (keepOpen) => {
    if (!quickForm.name.trim()) { toast.error("أدخل اسم الصنف"); quickNameRef.current?.focus(); return; }
    setQuickSaving(true);
    try {
      const body = {
        ...empty,
        name: quickForm.name.trim(),
        barcode: quickForm.barcode?.trim() || "",
        plu: quickForm.plu?.trim() || "",
        category: quickForm.category?.trim() || "",
        cost_price: +quickForm.cost_price || 0,
        sale_price: +quickForm.sale_price || 0,
        stock: +quickForm.stock || 0,
        currency: currentCompany?.base_currency || "SYP",
      };
      delete body.image_url;
      await api.post("/products/quick-add", body);
      setQuickCount(c => c + 1);
      toast.success(`تمت إضافة «${body.name}»`);
      if (keepOpen) {
        setQuickForm(quickEmpty);
        setTimeout(() => quickNameRef.current?.focus(), 30);
      } else {
        setQuickOpen(false);
      }
      load();
    } catch (e) { toast.error(formatErr(e)); } finally { setQuickSaving(false); }
  };

  const newBulkRow = () => ({ name: "", sale_price: "", cost_price: "", stock: "", barcode: "", category: "" });
  const openBulkAdd = () => { setBulkRows(Array.from({ length: 5 }, newBulkRow)); setBulkAddOpen(true); };
  const updateBulkRow = (index, key, value) => setBulkRows(rows => rows.map((row, i) => i === index ? { ...row, [key]: value } : row));
  const lookupBulkBarcode = async (index, rawBarcode) => {
    const barcode = String(rawBarcode || "").trim();
    if (!barcode) return;
    try {
      const { data } = await api.get("/global-products/lookup", { params: { barcode } });
      if (data?.found && data.name) {
        setBulkRows(rows => rows.map((row, i) => i === index && !row.name.trim() ? { ...row, name: data.name, barcode } : row));
        toast.success(`تم جلب اسم «${data.name}» من الفهرس العام`);
      }
    } catch { /* barcode lookup is advisory; saving the row remains available */ }
  };
  const removeBulkRow = (index) => setBulkRows(rows => rows.length > 1 ? rows.filter((_, i) => i !== index) : [newBulkRow()]);
  const pasteBulkRows = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text.trim()) return toast.info("انسخ أولاً صفوف الأصناف من Excel");
      const parsed = text.trim().split(/\r?\n/).map(line => line.split("\t").length > 1 ? line.split("\t") : line.split(","));
      const first = parsed[0].map(x => x.trim().toLowerCase());
      const hasHeader = first.some(x => ["الاسم", "الصنف", "name", "اسم الصنف"].includes(x));
      const rows = (hasHeader ? parsed.slice(1) : parsed).map(cols => ({
        name: (cols[0] || "").trim(), sale_price: (cols[1] || "").trim(), cost_price: (cols[2] || "").trim(),
        stock: (cols[3] || "").trim(), barcode: (cols[4] || "").trim(), category: (cols[5] || "").trim(),
      })).filter(row => Object.values(row).some(Boolean));
      if (rows.length) { setBulkRows(rows); toast.success(`تم لصق ${rows.length} صف`); }
    } catch { toast.error("تعذر قراءة الحافظة. استخدم زر اللصق أو أدخل الصفوف يدويًا"); }
  };
  const bulkSave = async () => {
    const rows = bulkRows.filter(row => row.name.trim());
    if (!rows.length) return toast.error("أدخل اسم صنف واحد على الأقل");
    const duplicateBarcodes = rows.filter(row => row.barcode && rows.filter(x => x.barcode === row.barcode).length > 1);
    if (duplicateBarcodes.length) return toast.error("يوجد باركود مكرر داخل الصفوف");
    setBulkSaving(true);
    try {
      const products = rows.map(row => ({
        name: row.name.trim(), barcode: row.barcode.trim() || undefined, category: row.category.trim(),
        sale_price: Number(row.sale_price) || 0, cost_price: Number(row.cost_price) || 0, stock: Number(row.stock) || 0,
        currency: currentCompany?.base_currency || "SYP", min_stock: 0, units: [{ name: "قطعة", factor: 1 }],
      }));
      if (MOCK_MODE) {
        const cid = localStorage.getItem("company_id") || "demo-pharma-a"; const all = readMock(MOCK_PRODUCTS_KEY, {}); const current = all[cid] || [];
        all[cid] = [...current, ...products.map((p, i) => ({ ...p, id: `mock-bulk-${Date.now()}-${i}`, barcode: p.barcode || `${Date.now()}${i}` }))]; writeMock(MOCK_PRODUCTS_KEY, all);
      } else await api.post("/products/bulk", { products });
      toast.success(`تمت إضافة ${products.length} صنف بنجاح`); setBulkAddOpen(false); setBulkRows([]); load();
    } catch (e) { toast.error(formatErr(e)); } finally { setBulkSaving(false); }
  };

  const del = async (id) => {
    if (!confirm("حذف الصنف؟")) return;
    try { await api.delete(`/products/${id}`); toast.success("تم الحذف"); load(); } catch(e){ toast.error(formatErr(e)); }
  };

  const bulkDelete = async () => {
    const ids = bulk.selectedIds;
    if (!ids.length) return;
    if (!confirm(`حذف ${ids.length} صنف؟ لا يمكن التراجع.`)) return;
    try {
      const { data } = await api.post("/products/bulk-delete", { ids });
      toast.success(`تم حذف ${data.deleted} صنف`);
      bulk.clear();
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const bulkAssignCategory = async () => {
    const ids = bulk.selectedIds;
    if (!ids.length) return;
    const cat = assignCat === "__clear__" ? "" : assignCat;
    try {
      const { data } = await api.post("/products/bulk-set-category", { product_ids: ids, category: cat });
      toast.success(`تم تعديل الفئة لـ ${data.updated} صنف`);
      bulk.clear();
      setAssignCat("");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const bulkShare = () => {
    const ids = new Set(bulk.selectedIds);
    const rows = items.filter(p => ids.has(p.id));
    if (!rows.length) return;
    const lines = rows.map(p => `• ${p.name} — ${p.barcode || "بدون باركود"} — ${p.sale_price?.toLocaleString?.("ar-EG")} ${p.currency}${p.stock != null ? ` — مخزون ${p.stock}` : ""}`);
    const text = `*قائمة الأصناف (${rows.length})*\n\n${lines.join("\n")}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  const bulkPrintLabels = () => {
    const ids = new Set(bulk.selectedIds);
    const rows = items.filter(p => ids.has(p.id));
    if (!rows.length) return;
    const tplKey = localStorage.getItem("label_tpl") || "medium";
    const res = printBarcodeLabels(rows, tplKey);
    if (res.ok) toast.success(`جاري تجهيز ${res.count} ملصق`);
    else if (res.reason === "popup_blocked") toast.error("النوافذ المنبثقة محجوبة — فعّل السماح لهذا الموقع");
  };

  const downloadTemplate = async () => {
    try {
      const XLSX = await import("xlsx");
      const headers = ["الاسم", "الباركود", "المجموعات", "التكلفة", "البيع", "العملة", "الرصيد", "الحد الأدنى", "دواء؟", "الصلاحية", "رقم التشغيلة"];
      // one illustrative example row to guide the user (they can delete it)
      const example = {
        "الاسم": "مثال: مياه معدنية 1 لتر", "الباركود": "6210000000001", "المجموعات": "مشروبات، أساسيات",
        "التكلفة": 100, "البيع": 150, "العملة": "SYP", "الرصيد": 50, "الحد الأدنى": 10,
        "دواء؟": "لا", "الصلاحية": "2027-12-31", "رقم التشغيلة": "",
      };
      const ws = XLSX.utils.json_to_sheet([example], { header: headers });
      ws["!cols"] = [{ wch: 28 }, { wch: 16 }, { wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 8 }, { wch: 10 }, { wch: 12 }, { wch: 8 }, { wch: 12 }, { wch: 16 }];
      ws["!rtl"] = true;
      const wb = XLSX.utils.book_new();
      wb.Workbook = { Views: [{ RTL: true }] };
      XLSX.utils.book_append_sheet(wb, ws, "قالب الأصناف");
      XLSX.writeFile(wb, "قالب_استيراد_الأصناف.xlsx");
      toast.success("تم تنزيل القالب — عبّئ الأصناف ثم استوردها");
    } catch (e) {
      toast.error("فشل تنزيل القالب: " + (e?.message || e));
    }
  };

  const exportRowsExcel = async (rows) => {
    if (!rows.length) { toast.error("لا توجد أصناف للتصدير"); return; }
    try {
      const XLSX = await import("xlsx");
      const headers = ["الاسم", "الباركود", "المجموعات", "التكلفة", "البيع", "العملة", "الرصيد", "الحد الأدنى", "دواء؟", "الصلاحية", "رقم التشغيلة"];
      const data = rows.map(p => {
        const cats = (p.categories && p.categories.length ? p.categories : (p.category ? [p.category] : [])).join("، ");
        return {
          "الاسم": p.name,
          "الباركود": p.barcode || "",
          "المجموعات": cats,
          "التكلفة": Number(p.cost_price) || 0,
          "البيع": Number(p.sale_price) || 0,
          "العملة": p.currency,
          "الرصيد": Number(p.stock) || 0,
          "الحد الأدنى": Number(p.min_stock) || 0,
          "دواء؟": p.is_pharma ? "نعم" : "لا",
          "الصلاحية": p.expiry_date || "",
          "رقم التشغيلة": p.batch_number || "",
        };
      });
      const ws = XLSX.utils.json_to_sheet(data, { header: headers });
      // Right-to-left worksheet + column widths
      ws["!cols"] = [
        { wch: 28 }, { wch: 16 }, { wch: 20 }, { wch: 12 }, { wch: 12 },
        { wch: 8 }, { wch: 10 }, { wch: 12 }, { wch: 8 }, { wch: 12 }, { wch: 16 }
      ];
      ws["!rtl"] = true;
      const wb = XLSX.utils.book_new();
      wb.Workbook = { Views: [{ RTL: true }] };
      XLSX.utils.book_append_sheet(wb, ws, "المنتجات");
      XLSX.writeFile(wb, `products_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`تم تصدير ${rows.length} صنف إلى Excel (xlsx)`);
    } catch (e) {
      toast.error("فشل التصدير: " + (e?.message || e));
    }
  };
  const bulkExportExcel = async () => {
    const ids = new Set(bulk.selectedIds);
    await exportRowsExcel(items.filter(p => ids.has(p.id)));
  };

  const bulkAdjustPrices = async () => {
    const ids = bulk.selectedIds;
    if (!ids.length) return;
    if (priceMode === "percent" && (!Number.isFinite(+pricePct) || !pricePct || +pricePct < -100)) return toast.error("أدخل نسبة صحيحة، ولا تخفض السعر بأكثر من 100٪");
    if (priceMode === "amount" && (!Number.isFinite(+priceAmount) || +priceAmount <= 0)) return toast.error("أدخل مبلغاً أكبر من صفر");
    if (priceMode === "amount" && mixedPriceCurrencies) return toast.error("حدّد أصنافاً بعملة واحدة لتطبيق مبلغ ثابت بأمان");
    try {
      const payload = {
        ids, mode: priceMode, target: priceTarget,
        round_to: +priceRound > 0 ? +priceRound : null,
        reason: priceReason || "",
      };
      if (priceMode === "percent") payload.percent = +pricePct;
      else { payload.amount = +priceAmount; payload.direction = priceDirection; }
      const { data } = await api.post("/products/bulk-adjust-prices", payload);
      const changeLabel = priceMode === "percent"
        ? `${+pricePct > 0 ? "بنسبة +" : "بنسبة "}${pricePct}%`
        : `${priceDirection === "increase" ? "برفع" : "بخفض"} ${fmtMoney(+priceAmount, data.currency || priceCurrency)}`;
      toast.success(`تم تعديل أسعار ${data.updated} صنف ${changeLabel}`);
      setPriceOpen(false);
      setPriceReason("");
      setPriceAmount("");
      bulk.clear();
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const uploadImage = async (pid, file, inputEl) => {
    if (!file) return;
    if (file.size > 1024 * 1024) { toast.error("الحد الأقصى 1MB"); return; }
    const b64 = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result.split(",")[1]); fr.readAsDataURL(file); });
    try {
      await api.post(`/products/${pid}/image`, { content_type: file.type || "image/png", data_b64: b64 });
      toast.success("تم رفع الصورة"); load();
    } catch (e) { toast.error(formatErr(e)); }
    if (inputEl) inputEl.value = "";
  };

  const addUnit = () => setForm(f => ({...f, units: [...f.units, {name:"", factor:1}]}));
  const rmUnit = (i) => setForm(f => ({...f, units: f.units.filter((_,x)=>x!==i)}));

  const printLabel = (p) => {
    const w = window.open("", "_blank");
    w.document.write(`
      <html dir="rtl"><head><title>باركود ${p.name}</title>
      <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
      <style>body{font-family:Cairo, sans-serif;text-align:center;padding:16px}</style>
      </head><body>
      <div><strong>${p.name}</strong></div>
      <svg id="bc"></svg>
      <div>${fmtMoney(p.sale_price, p.currency)}</div>
      <script>JsBarcode("#bc","${p.barcode}",{format:"CODE128",width:2,height:60})</script>
      <script>window.onload=()=>setTimeout(()=>window.print(),300)</script>
      </body></html>
    `);
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">{t("products_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("products_subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          {aiImportEnabled && <Button variant="outline" onClick={()=>setAiOpen(true)} data-testid="ai-import-button" className="gap-1 border-primary text-primary">
            <Sparkles className="w-4 h-4" /> {t("ai_add")}
          </Button>}
          {bulkImportEnabled && <Button variant="outline" onClick={()=>document.getElementById("xlsx-input").click()} data-testid="import-products-button" className="gap-1">
            <Upload className="w-4 h-4" /> {t("import_excel")}
          </Button>}
          <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-button" className="gap-1">
            <Download className="w-4 h-4" /> قالب فارغ
          </Button>
          <Button variant="outline" onClick={()=>exportRowsExcel(items)} data-testid="export-all-products-button" className="gap-1">
            <Download className="w-4 h-4" /> تصدير الكل
          </Button>
          <input id="xlsx-input" type="file" accept=".xlsx,.xls" hidden onChange={async (e)=>{
            const f = e.target.files?.[0]; if (!f) return;
            const fd = new FormData(); fd.append("file", f);
            const cid = localStorage.getItem("company_id"); const token = localStorage.getItem("access_token");
            try {
              const res = await fetch(`${API}/products/import`, { method:"POST", body: fd, credentials:"include", headers:{ "X-Company-Id": cid, "Authorization": `Bearer ${token}` }});
              const data = await res.json();
              if (!res.ok) throw new Error(data.detail || "فشل");
              toast.success(`تم استيراد ${data.created} صنف جديد + تحديث ${data.updated}${data.errors?.length ? ` (${data.errors.length} أخطاء)` : ""}`);
              load();
            } catch(err) { toast.error(err.message); }
            e.target.value = "";
          }} />
          <Button variant="outline" onClick={()=>setScanOpen(true)} data-testid="scan-to-add-button" className="gap-1">
            <Camera className="w-4 h-4" /> {t("scan_to_add")}
          </Button>
          <Button variant="outline" onClick={openReorder} data-testid="reorder-button" className="gap-1 border-amber-300 text-amber-700">
            <ClipboardList className="w-4 h-4" /> إعادة طلب النواقص
          </Button>
          <BulkProductDialog
            enabled={bulkAddEnabled}
            open={bulkAddOpen}
            setOpen={setBulkAddOpen}
            rows={bulkRows}
            setRows={setBulkRows}
            saving={bulkSaving}
            onOpen={openBulkAdd}
            onPaste={pasteBulkRows}
            onUpdate={updateBulkRow}
            onBarcodeBlur={lookupBulkBarcode}
            onRemove={removeBulkRow}
            newRow={newBulkRow}
            onSave={bulkSave}
          />
          <QuickProductDialog
            enabled={quickAddEnabled}
            open={quickOpen}
            setOpen={setQuickOpen}
            form={quickForm}
            setForm={setQuickForm}
            count={quickCount}
            inputRef={quickNameRef}
            saving={quickSaving}
            onSave={quickSave}
          />
          <ProductFormDialog
            open={open}
            setOpen={setOpen}
            openNew={openNew}
            editing={editing}
            t={t}
            form={form}
            setForm={setForm}
            formImage={formImage}
            setFormImage={setFormImage}
            pickFormImage={pickFormImage}
            lookupGlobalBarcode={lookupGlobalBarcode}
            scanForFormOpen={scanForFormOpen}
            setScanForFormOpen={setScanForFormOpen}
            currencyOptions={currencyOptions}
            profit={profit}
            margin={margin}
            sellByWeight={sellByWeight}
            toggleSellByWeight={toggleSellByWeight}
            categories={categories}
            addUnit={addUnit}
            rmUnit={rmUnit}
            save={save}
          />
        </div>
      </div>

      <BarcodeScannerDialog open={scanOpen} onClose={()=>setScanOpen(false)} onScan={handleScanned} />

      <Dialog open={reorderOpen} onOpenChange={setReorderOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="reorder-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ClipboardList className="w-5 h-5 text-amber-600" /> طلب شراء — الأصناف الناقصة</DialogTitle></DialogHeader>
          {reorderItems.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground" data-testid="reorder-empty">لا توجد أصناف ناقصة 🎉 (كل الأصناف فوق حدّها الأدنى)</div>
          ) : (
            <>
              <div className="text-xs text-muted-foreground mb-2">الكمية المقترحة = ضعف الحد الأدنى − المتوفر. عدّل قبل الطباعة إن رغبت.</div>
              <table className="w-full text-sm border rounded-md overflow-hidden">
                <thead className="bg-muted text-xs"><tr><th className="p-2 text-start">الصنف</th><th className="p-2">المتوفر</th><th className="p-2">الأدنى</th><th className="p-2">المطلوب</th></tr></thead>
                <tbody>
                  {reorderItems.map((x, i) => (
                    <tr key={x.id} className="border-t" data-testid={`reorder-item-${x.id}`}>
                      <td className="p-2">{x.name}</td>
                      <td className="p-2 text-center text-red-600 font-bold">{x.stock}</td>
                      <td className="p-2 text-center">{x.min_stock}</td>
                      <td className="p-2 text-center">
                        <Input type="number" value={x.suggested} onChange={e=>setReorderItems(arr=>arr.map((y,j)=>j===i?{...y, suggested:+e.target.value}:y))} className="w-20 h-8 text-center mx-auto" data-testid={`reorder-qty-${x.id}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setReorderOpen(false)}>إغلاق</Button>
            {reorderItems.length > 0 && <Button onClick={printReorder} className="gap-2 bg-amber-600 hover:bg-amber-700" data-testid="reorder-print"><Printer className="w-4 h-4" /> طباعة طلب الشراء</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BarcodeScannerDialog open={scanForFormOpen} onClose={()=>setScanForFormOpen(false)} onScan={(code)=>{ setScanForFormOpen(false); setForm(f=>({...f, barcode: String(code).trim()})); toast.success("تم قراءة الباركود"); }} />
      <AIImportProducts open={aiOpen} onClose={()=>setAiOpen(false)} onDone={load} />

      <div className="relative max-w-md">
        <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder={t("search_products")} value={search} onChange={(e)=>setSearch(e.target.value)} className="ps-9" data-testid="products-search-input" />
      </div>

      {categories.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap p-3 border border-dashed border-primary/30 rounded-md bg-primary/5" data-testid="cat-drop-strip">
          <span className="text-xs font-bold text-primary shrink-0">↖ اسحب أي صنف إلى مجموعة:</span>
          {categories.map(c => (
            <button
              key={c.id}
              onDragOver={(e) => {
                if (!e.dataTransfer.types.includes("application/x-muhasabaty-products")) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "copy";
                e.currentTarget.classList.add("ring-2","ring-primary","ring-offset-2","scale-105");
              }}
              onDragLeave={(e)=>e.currentTarget.classList.remove("ring-2","ring-primary","ring-offset-2","scale-105")}
              onDrop={async (e) => {
                e.preventDefault();
                e.currentTarget.classList.remove("ring-2","ring-primary","ring-offset-2","scale-105");
                const raw = e.dataTransfer.getData("application/x-muhasabaty-products");
                if (!raw) return;
                let payload;
                try { payload = JSON.parse(raw); } catch { return; }
                const ids = payload.product_ids || [];
                if (!ids.length) return;
                try {
                  const { data } = await api.post(`/categories/${c.id}/attach-products`, { ids });
                  toast.success(`تمت إضافة ${data.updated} صنف إلى «${c.name}»`);
                  bulk.clear();
                  load();
                } catch (err) { toast.error(formatErr(err)); }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all hover:shadow-sm"
              style={{ background: `${c.color}15`, borderColor: `${c.color}55`, color: c.color }}
              data-testid={`cat-drop-${c.id}`}
              title={`أفلت هنا لإضافة الأصناف إلى «${c.name}»`}
              type="button"
            >
              <span className="w-4 h-4 grid place-items-center rounded-full" style={{background: c.color}}><CategoryIcon name={c.icon || "Package"} size={10} className="text-white" /></span>
              {c.name}
              <span className="text-[10px] opacity-70">({c.products_count || 0})</span>
            </button>
          ))}
        </div>
      )}

      <BulkSelectionBar bulk={bulk} label="صنف">
        <div className="flex items-center gap-1.5 border border-primary/30 rounded-md ps-2 bg-white/50 dark:bg-slate-900/50">
          <FolderInput className="w-3.5 h-3.5 text-primary" />
          <Select value={assignCat} onValueChange={setAssignCat}>
            <SelectTrigger className="h-8 w-40 border-0 bg-transparent focus:ring-0" data-testid="bulk-assign-cat-select">
              <SelectValue placeholder="اختر مجموعة" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__clear__">— بدون فئة —</SelectItem>
              {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="ghost" onClick={() => bulkAssignCategory()} disabled={!assignCat} className="h-8" data-testid="bulk-assign-cat-apply">تعيين</Button>
        </div>
        <Button size="sm" variant="outline" onClick={bulkPrintLabels} className="gap-1 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="bulk-print-labels-btn"><Printer className="w-3.5 h-3.5" /> طباعة ملصقات</Button>
        <Button size="sm" variant="outline" onClick={() => setPriceOpen(true)} className="gap-1 border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="bulk-price-btn"><DollarSign className="w-3.5 h-3.5" /> تعديل الأسعار</Button>
        <Button size="sm" variant="outline" onClick={bulkExportExcel} className="gap-1 border-slate-400 text-slate-700 hover:bg-slate-50" data-testid="bulk-export-btn"><Download className="w-3.5 h-3.5" /> تصدير Excel</Button>
        <Button size="sm" variant="outline" onClick={bulkShare} className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="bulk-share-btn"><Share2 className="w-3.5 h-3.5" /> مشاركة</Button>
        <Button size="sm" variant="destructive" onClick={bulkDelete} className="gap-1" data-testid="bulk-delete-btn"><Trash2 className="w-3.5 h-3.5" /> حذف المحدد</Button>
      </BulkSelectionBar>

      <div className="border border-border rounded-md bg-card overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-10"><SelectAllHeader bulk={bulk} /></TableHead>
            <TableHead className="text-start">{t("image") || "صورة"}</TableHead>
            <TableHead className="text-start">{t("col_product")}</TableHead>
            <TableHead className="text-start">{t("barcode")}</TableHead>
            <TableHead className="text-start">PLU</TableHead>
            <TableHead className="text-start">{t("col_cost")}</TableHead>
            <TableHead className="text-start">{t("col_price")}</TableHead>
            <TableHead className="text-start">{t("col_stock")}</TableHead>
            <TableHead className="text-start">{t("col_expiry")}</TableHead>
            <TableHead className="text-start">{t("actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">{t("no_products")}</TableCell></TableRow>}
            {items.map(p => <ProductRow
              key={p.id}
              p={p}
              bulk={bulk}
              categories={categories}
              rates={rates}
              imgSrc={imgSrc}
              fmtMoney={fmtMoney}
              onUploadImage={uploadImage}
              onPrintLabel={printLabel}
              onEdit={openEdit}
              onDuplicate={duplicateProduct}
              onDelete={(product) => del(product.id)}
            />)}
          </TableBody>
        </Table>
      </div>

      <BulkPriceDialog
        open={priceOpen}
        setOpen={setPriceOpen}
        count={bulk.count}
        mode={priceMode}
        setMode={setPriceMode}
        pct={pricePct}
        setPct={setPricePct}
        amount={priceAmount}
        setAmount={setPriceAmount}
        direction={priceDirection}
        setDirection={setPriceDirection}
        target={priceTarget}
        setTarget={setPriceTarget}
        round={priceRound}
        setRound={setPriceRound}
        reason={priceReason}
        setReason={setPriceReason}
        currency={priceCurrency}
        mixedCurrencies={mixedPriceCurrencies}
        currencies={selectedCurrencies}
        selectedProducts={selectedProducts}
        fmtMoney={fmtMoney}
        previewAdjustedPrice={previewAdjustedPrice}
        onApply={bulkAdjustPrices}
      />
    </div>
  );
}
